import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql, withPrefix } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledEducationSection = styled.section`
  max-width: 1000px;

  .inner {
    display: grid;
    grid-template-columns: minmax(310px, 460px) minmax(0, 1fr);
    gap: 70px;
    align-items: center;

    @media (max-width: 1080px) {
      gap: 40px;
    }

    @media (max-width: 768px) {
      display: block;
    }
  }
`;

const StyledPic = styled.div`
  position: relative;
  width: 104%;
  max-width: 580px;
  justify-self: start;

  @media (max-width: 768px) {
    margin: 0 auto 50px;
    width: 75%;
    max-width: 420px;
  }

  @media (max-width: 480px) {
    width: 100%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      display: block;
      width: 100%;
      height: auto;
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(80%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const StyledText = styled.div`
  .school {
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    margin-bottom: 12px;
  }

  .degree {
    margin: 0 0 8px;
    color: var(--lightest-slate);
    font-size: clamp(24px, 4vw, 32px);
    line-height: 1.2;
  }

  .meta {
    display: grid;
    gap: 4px;
    margin-bottom: 24px;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
  }

  .education-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(160px, 220px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 25px 0 0 0;
    list-style: none;

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;

const prefixAssetPath = url => {
  if (!url) {
    return url;
  }

  const pathPrefix = withPrefix('/').replace(/\/$/, '');
  const isLocalRootPreview =
    typeof window !== 'undefined' && pathPrefix && !window.location.pathname.startsWith(pathPrefix);

  if (isLocalRootPreview && url.startsWith(`${pathPrefix}/static/`)) {
    return url.replace(pathPrefix, '');
  }

  return url.startsWith('/static/') ? withPrefix(url) : url;
};

const Education = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const data = useStaticQuery(graphql`
    {
      purdue: file(relativePath: { eq: "purdue.png" }) {
        publicURL
      }
    }
  `);
  const purdue = prefixAssetPath(data.purdue?.publicURL);

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  const highlights = [
    'Cybersecurity',
    'Network Engineering',
    'Systems Administration',
    'Secure Infrastructure',
    'Hands-on Experience',
    'Technical Documentation',
  ];

  return (
    <StyledEducationSection id="education" ref={revealContainer}>
      <h2 className="numbered-heading">Education</h2>

      <div className="inner">
        <StyledPic>
          <div className="wrapper">
            <img src={purdue} className="img" alt="Purdue University entrance arch" />
          </div>
        </StyledPic>

        <StyledText>
          <p className="school">Purdue University Polytechnic Institute</p>
          <h3 className="degree">B.S. in Cybersecurity &amp; Network Engineering</h3>
          <div className="meta">
            <span>Expected Graduation: December 2026</span>
            <span>West Lafayette, Indiana</span>
          </div>

          <p>
            I’m studying cybersecurity, network engineering, systems administration, and secure
            infrastructure through a mix of coursework, labs, and hands-on projects.
          </p>

          <p>
            My academic work has focused on networking, security fundamentals, troubleshooting,
            technical documentation, and building practical experience in lab-based environments.
          </p>

          <ul className="education-list">
            {highlights.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </StyledText>
      </div>
    </StyledEducationSection>
  );
};

export default Education;
